import React, { useState, useEffect } from 'react';
import { BarChart3, Filter, Download, TrendingUp, DollarSign, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import ProfitabilityTables from '@/components/ProfitabilityTables';
import NotificationBell from '@/components/NotificationBell';

export default function ProfitabilityAnalysis() {
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [profitabilityTables, setProfitabilityTables] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonData, setComparisonData] = useState([]);

  useEffect(() => {
    fetchProposals();
  }, []);

  useEffect(() => {
    if (selectedProposal) {
      fetchScenarios(selectedProposal);
    }
  }, [selectedProposal]);

  const fetchProposals = async () => {
    try {
      setLoadingProposals(true);
      const response = await api.get('/collections');
      const activeProposals = (response.data.collections || []).filter(p => !p.deleted);
      setProposals(activeProposals);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoadingProposals(false);
    }
  };

  const fetchScenarios = async (proposalId) => {
    try {
      const response = await api.get(`/scenarios?collection_id=${proposalId}`);
      setScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      toast.error('Failed to load scenarios');
    }
  };

  const calculateProfitability = async () => {
    if (!selectedScenario) {
      toast.error('Please select a scenario to analyze');
      return;
    }

    try {
      setLoading(true);
      const scenario = scenarios.find(s => s.id === selectedScenario);
      
      if (!scenario) {
        toast.error('Scenario not found');
        return;
      }

      const scenarioInfo = scenario.scenario_info || {};
      const volumeAssumptions = scenario.volume_assumptions || {};
      const proposedPricing = scenario.proposed_pricing || {};
      const transactionDistribution = scenario.transaction_distribution || [];

      const transformedDistribution = transactionDistribution.map(item => ({
        collectionAmount: item.collectionAmount,
        low: item.low,
        mid: item.mid,
        high: item.high,
        pct: item.currentPercent || item.pct || 0
      }));

      const calculationPayload = {
        annual_transactions: volumeAssumptions.totalTransactions || 10000,
        service_rate: 0.02,
        collection_amounts: transformedDistribution,
        payment_rates: proposedPricing.paymentRates || {},
        volume_mix: {
          ACH: (volumeAssumptions.achPercent || 0) / 100,
          CC: (volumeAssumptions.ccPercent || 0) / 100,
          AMEX: (volumeAssumptions.amexPercent || 0) / 100,
          DEBIT: (volumeAssumptions.debitPercent || 0) / 100
        },
        merchant_fees: {
          ACH: proposedPricing.paymentRates?.ACH?.merchantFee || 0,
          CC: proposedPricing.paymentRates?.CC?.merchantFee || 0,
          AMEX: proposedPricing.paymentRates?.AMEX?.merchantFee || 0,
          DEBIT: proposedPricing.paymentRates?.DEBIT?.merchantFee || 0
        },
        ach_fee_type: scenarioInfo.achFeeType || 'percentage'
      };

      const response = await api.post('/fee-table/profitability-tables', calculationPayload);
      
      if (response.data.success) {
        setProfitabilityTables(response.data.tables);
        toast.success('Profitability analysis complete');
        
        // Add to comparison if in comparison mode
        if (comparisonMode) {
          setComparisonData(prev => [...prev, {
            scenario: scenario.name,
            serviceName: scenario.service_name,
            tables: response.data.tables
          }]);
        }
      }
    } catch (error) {
      console.error('Error calculating profitability:', error);
      toast.error('Failed to calculate profitability');
    } finally {
      setLoading(false);
    }
  };

  const clearComparison = () => {
    setComparisonData([]);
    setComparisonMode(false);
  };

  const exportToExcel = () => {
    toast.info('Excel export coming soon');
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                <BarChart3 className="w-7 h-7 text-indigo-600" />
                Profitability Analysis Center
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Analyze payment method profitability and margins by proposal or scenario
              </p>
            </div>
            <NotificationBell />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters Section */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Select Proposal & Scenario</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Proposal Selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Proposal
              </label>
              <select
                value={selectedProposal || ''}
                onChange={(e) => {
                  setSelectedProposal(e.target.value);
                  setSelectedScenario(null);
                  setProfitabilityTables(null);
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={loadingProposals}
              >
                <option value="">Select a proposal...</option>
                {proposals.map(proposal => (
                  <option key={proposal.id} value={proposal.id}>
                    {proposal.name || proposal.collection_name || `Proposal ${proposal.id}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Scenario Selector */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Scenario / Service
              </label>
              <select
                value={selectedScenario || ''}
                onChange={(e) => {
                  setSelectedScenario(e.target.value);
                  setProfitabilityTables(null);
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={!selectedProposal || scenarios.length === 0}
              >
                <option value="">Select a scenario...</option>
                {scenarios.map(scenario => (
                  <option key={scenario.id} value={scenario.id}>
                    {scenario.name} - {scenario.service_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2">
              <button
                onClick={calculateProfitability}
                disabled={!selectedScenario || loading}
                className="btn-primary flex items-center gap-2 flex-1"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4" />
                    Analyze
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Comparison Mode Toggle */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={comparisonMode}
                onChange={(e) => setComparisonMode(e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-slate-700">
                Comparison Mode (analyze multiple scenarios side-by-side)
              </span>
            </label>
            {comparisonMode && comparisonData.length > 0 && (
              <button
                onClick={clearComparison}
                className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Clear Comparison ({comparisonData.length} items)
              </button>
            )}
          </div>
        </div>

        {/* Info Banner */}
        {!profitabilityTables && !comparisonData.length && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Welcome to Profitability Analysis</h3>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Select a proposal and scenario above, then click <strong>"Analyze"</strong> to view detailed profitability breakdowns by payment method.
              Enable comparison mode to analyze multiple scenarios side-by-side.
            </p>
          </div>
        )}

        {/* Single Analysis Results */}
        {!comparisonMode && profitabilityTables && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Analysis Results</h2>
                <p className="text-sm text-slate-600">
                  Showing profitability for selected scenario
                </p>
              </div>
              <button
                onClick={exportToExcel}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export to Excel
              </button>
            </div>

            <ProfitabilityTables tables={profitabilityTables} />
          </div>
        )}

        {/* Comparison Mode Results */}
        {comparisonMode && comparisonData.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Comparison Results</h2>
                <p className="text-sm text-slate-600">
                  Comparing {comparisonData.length} scenario{comparisonData.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={exportToExcel}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Comparison
              </button>
            </div>

            {/* Comparison Table */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Scenario</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Service</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">CC GPM</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">ACH GPM</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Combined GPM</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-900">Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((item, index) => (
                      <tr key={index} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.scenario}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{item.serviceName}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-purple-600">
                          {formatPercent(item.tables.cc_based.gpm)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-blue-600">
                          {formatPercent(item.tables.ach_only.gpm)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-emerald-600">
                          {formatPercent(item.tables.combined.gpm)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-slate-900">
                          {formatCurrency(item.tables.combined.gross_revenue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Detailed views for each comparison item */}
            {comparisonData.map((item, index) => (
              <div key={index} className="border-t-4 border-indigo-500 pt-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">
                  {item.scenario} - {item.serviceName}
                </h3>
                <ProfitabilityTables tables={item.tables} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
